import {
    styled,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableSortLabel,
} from '@mui/material';
import { ficheRenseignementEntryType } from '../../../lib/api/api';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { IconButton } from '../../../components/IconButton';
import { useNavigate } from 'react-router-dom';
import { pathHandler } from '../../../lib/pathHandler';
import { useState } from 'react';
import { useAlert } from '../../../lib/alert';

type orderByColumnType = 'createdAt' | 'firstName' | 'lastName' | 'selectedPromo';

type orderByType = { column: orderByColumnType; direction: 'asc' | 'desc' };

function FicheRenseignementTable(props: {
    ficheRenseignementEntries: ficheRenseignementEntryType[];
}) {
    const navigate = useNavigate();
    const [orderBy, setOrderBy] = useState<orderByType | undefined>({
        column: 'createdAt',
        direction: 'desc',
    });
    const { displayAlert } = useAlert();

    const sortedFicheRenseignementEntries = sortData(props.ficheRenseignementEntries);

    return (
        <Table>
            <TableHead>
                <TableRow>
                    <TableCell>
                        <TableSortLabel
                            active={orderBy?.column === 'createdAt'}
                            direction={orderBy?.direction}
                            onClick={buildSortHandler('createdAt')}
                        >
                            Date de réception
                        </TableSortLabel>
                    </TableCell>
                    <TableCell>
                        <TableSortLabel
                            active={orderBy?.column === 'firstName'}
                            direction={orderBy?.direction}
                            onClick={buildSortHandler('firstName')}
                        >
                            Prénom
                        </TableSortLabel>
                    </TableCell>
                    <TableCell>
                        <TableSortLabel
                            active={orderBy?.column === 'lastName'}
                            direction={orderBy?.direction}
                            onClick={buildSortHandler('lastName')}
                        >
                            NOM
                        </TableSortLabel>
                    </TableCell>
                    <TableCell>Adresse e-mail</TableCell>
                    <TableCell>
                        <TableSortLabel
                            active={orderBy?.column === 'selectedPromo'}
                            direction={orderBy?.direction}
                            onClick={buildSortHandler('selectedPromo')}
                        >
                            Promotion
                        </TableSortLabel>
                    </TableCell>
                    <TableCell></TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {sortedFicheRenseignementEntries.map((ficheRenseignementEntry) => {
                    const rowData = computeRowData(ficheRenseignementEntry);
                    return (
                        <TableRow key={ficheRenseignementEntry.id}>
                            {rowData.map((cellData, index) => (
                                <TableCell key={index}>{cellData}</TableCell>
                            ))}
                            <TableCell>
                                <CellRow>
                                    <IconButton
                                        color="primary"
                                        size="small"
                                        title="Copier la ligne"
                                        IconComponent={ContentCopyIcon}
                                        onClick={() => copyRowToClipboard(rowData)}
                                    />
                                    <IconButton
                                        color="primary"
                                        size="small"
                                        title="Aller au formulaire"
                                        IconComponent={ArrowForwardIcon}
                                        onClick={() =>
                                            navigateToEntryPreview(ficheRenseignementEntry.id)
                                        }
                                    />
                                </CellRow>
                            </TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    );

    function sortData(data: ficheRenseignementEntryType[]) {
        if (orderBy === undefined) {
            return data;
        }
        return data.sort((a, b) => {
            const aValue = a[orderBy.column];
            const bValue = b[orderBy.column];
            if (aValue === bValue) {
                return 0;
            }
            const comparison = aValue.localeCompare(bValue);

            return orderBy.direction === 'asc' ? comparison : -comparison;
        });
    }

    function buildSortHandler(orderByColumn: orderByColumnType) {
        return () => {
            if (orderBy === undefined || orderBy.column !== orderByColumn) {
                setOrderBy({ column: orderByColumn, direction: 'asc' });
                return;
            } else {
                switch (orderBy.direction) {
                    case 'asc':
                        setOrderBy({ column: orderByColumn, direction: 'desc' });
                        break;
                    case 'desc':
                        setOrderBy({ column: orderByColumn, direction: 'asc' });
                        break;
                }
            }
        };
    }

    function computeRowData(ficheRenseignementEntry: ficheRenseignementEntryType): string[] {
        const rowData = [
            ficheRenseignementEntry.createdAt,
            ficheRenseignementEntry.firstName.toUpperCase(),
            ficheRenseignementEntry.lastName.toUpperCase(),
            ficheRenseignementEntry.email,
            ficheRenseignementEntry.selectedPromo,
        ];
        return rowData;
    }

    function navigateToEntryPreview(ficheRenseignementId: string) {
        const path = pathHandler.getRoutePath('FICHE_RENSEIGNEMENT_PREVIEW', {
            ficheRenseignementId,
        });
        navigate(path);
    }

    async function copyRowToClipboard(rowData: string[]) {
        const textToCopy = rowData.join('\t'); // Use tab as a separator for
        try {
            await navigator.clipboard.writeText(textToCopy);
            displayAlert({
                variant: 'success',
                text: 'Ligne copiée dans le presse-papiers.',
            });
        } catch (error) {
            displayAlert({
                variant: 'error',
                text: `Échec de la copie dans le presse-papiers: ${error}`,
            });
        }
    }
}

const CellRow = styled('div')(({ theme }) => ({
    display: 'flex',
}));

export { FicheRenseignementTable };
